import { getAnalysisURL } from '@modules/bookmark/helpers';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import {
  getDashboardLink,
  trackDashboardEvent,
} from '@modules/dashboard/helpers';
import { useUpsertDashboard } from '@modules/dashboard/hooks';
import { DashboardActionType } from '@modules/dashboard/store/dashboardActionType';
import { useDashboardStore } from '@modules/dashboard/store/useDashboardStore';
import HeightIcon from '@mui/icons-material/Height';
import { Button, Stack, Typography } from '@mui/material';
import {
  AnalysisViewFragment,
  DashboardAnalysisMetadataInput,
} from 'generated/graphql';
import { useGetUser } from 'v2/core/services/user';

interface ResizeDashboardFeedListProps {
  analysis: AnalysisViewFragment;
}

export function ResizeDashboardFeedList({
  analysis,
}: ResizeDashboardFeedListProps) {
  const {
    tenant: { slug: tenantSlug },
  } = useGetUser();
  const { showSuccessNotification } = useNotificationContext();
  const dispatch = useDashboardStore(({ dispatch }) => dispatch);
  const dashboardAnalysesMetadata = useDashboardStore(
    ({ metadata }) => metadata.analysesMetadata,
  );
  const currentAnalysisMetadata = dashboardAnalysesMetadata.find(
    (metadata) => metadata.id === analysis.id,
  );
  const dashboard = useDashboardStore(({ dashboard }) => dashboard);
  const { upsertDashboard } = useUpsertDashboard();

  const getUpdatedAnalysesMetadata = (): DashboardAnalysisMetadataInput[] => {
    return Boolean(currentAnalysisMetadata)
      ? dashboardAnalysesMetadata.map((metadata) =>
          metadata.id === analysis.id
            ? {
                ...metadata,
                shouldShowAsFeed: false,
              }
            : metadata,
        )
      : [
          ...dashboardAnalysesMetadata,
          {
            id: analysis.id,
            shouldShowAsFeed: false,
          },
        ];
  };

  const switchToSummaryView = async () => {
    const updatedAnalysesMetadata = getUpdatedAnalysesMetadata();

    dispatch({
      type: DashboardActionType.SET_METADATA,
      payload: {
        analysesMetadata: updatedAnalysesMetadata,
      },
    });

    if (dashboard?.editToken) {
      await upsertDashboard({
        dashboardProps: {
          metadataV2: {
            analysesMetadata: updatedAnalysesMetadata,
          },
        },
      });
      showSuccessNotification({
        contentComponent: 'Your changes have been saved successfully.',
        props: {},
      });
    }

    trackDashboardEvent({
      eventName: 'Dashboard - Toggled To Prompt Answer View',
      dashboardLink: dashboard ? getDashboardLink(dashboard, tenantSlug) : '',
      analysisLink: getAnalysisURL(analysis, tenantSlug),
      isFeedbackListView: true,
    });
  };

  return (
    <Stack
      justifyContent="center"
      alignItems="center"
      spacing={3}
      sx={{ width: '100%', height: '100%' }}
    >
      <Stack alignItems="center">
        <HeightIcon fontSize="large" sx={{ transform: 'rotate(90deg)' }} />
        <Typography variant="subtitle2">
          Widget too narrow for feedback list
        </Typography>
      </Stack>
      <Stack alignItems="center">
        <Button variant="outlined" size="small" onClick={switchToSummaryView}>
          Switch to Prompt Answer view
        </Button>
        <Typography variant="body2" color="text.secondary">
          or
        </Typography>
        <Typography variant="body2" color="text.secondary">
          increase the width of the widget
        </Typography>
      </Stack>
    </Stack>
  );
}
